"use client";

import * as React from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  Paper,
  Divider,
  Chip,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import GppGoodRoundedIcon from "@mui/icons-material/GppGoodRounded";
import { useI18n } from "i18n/I18nProvider";

type Section = {
  id: string;
  titleKey: string;
  bodyKey: string;
};

export default function PrivacyPolicyClient({ locale }: { locale: string }) {
  const { t } = useI18n();

  const sections: Section[] = React.useMemo(
    () => [
      {
        id: "overview",
        titleKey: "privacy_s1_title",
        bodyKey: "privacy_s1_body",
      },
      { id: "data", titleKey: "privacy_s2_title", bodyKey: "privacy_s2_body" },
      { id: "use", titleKey: "privacy_s3_title", bodyKey: "privacy_s3_body" },
      {
        id: "cookies",
        titleKey: "privacy_s4_title",
        bodyKey: "privacy_s4_body",
      },
      {
        id: "sharing",
        titleKey: "privacy_s5_title",
        bodyKey: "privacy_s5_body",
      },
      {
        id: "retention",
        titleKey: "privacy_s6_title",
        bodyKey: "privacy_s6_body",
      },
      {
        id: "rights",
        titleKey: "privacy_s7_title",
        bodyKey: "privacy_s7_body",
      },
      {
        id: "security",
        titleKey: "privacy_s8_title",
        bodyKey: "privacy_s8_body",
      },
      {
        id: "contact",
        titleKey: "privacy_s9_title",
        bodyKey: "privacy_s9_body",
      },
    ],
    [],
  );

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Stack spacing={1.2} sx={{ mb: 3 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip
              icon={<GppGoodRoundedIcon />}
              label={t("privacy_badge")}
              sx={{
                borderRadius: 999,
                fontWeight: 900,
                bgcolor: "action.selected",
              }}
            />
            <Chip
              label={t("privacy_badge_premium")}
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 800 }}
            />
          </Stack>

          <Typography
            variant="h3"
            sx={{ fontWeight: 950, letterSpacing: -0.8, lineHeight: 1.05 }}
          >
            {t("privacy_title")}
          </Typography>

          <Typography
            color="text.secondary"
            sx={{ maxWidth: 860, lineHeight: 1.7 }}
          >
            {t("privacy_intro")}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t("privacy_last_updated")}
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2.5}
          alignItems="flex-start"
        >
          {/* TOC */}
          <Paper
            elevation={0}
            sx={{
              width: { xs: "100%", md: 320 },
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              position: { md: "sticky" },
              top: { md: 96 },
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 950, mb: 1 }}>
                {t("privacy_toc_title")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                {t("privacy_toc_hint")}
              </Typography>

              <Divider sx={{ mb: 1 }} />

              <List disablePadding>
                {sections.map((s) => (
                  <ListItemButton
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    sx={{ borderRadius: 2, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={t(s.titleKey)}
                      primaryTypographyProps={{ fontWeight: 800 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Paper>

          {/* Content */}
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              {sections.map((s, idx) => (
                <Box key={s.id} id={s.id} sx={{ scrollMarginTop: 96 }}>
                  <Typography variant="h6" sx={{ fontWeight: 950, mb: 1 }}>
                    {t(s.titleKey)}
                  </Typography>

                  <Typography
                    color="text.secondary"
                    sx={{ lineHeight: 1.8, whiteSpace: "pre-line" }}
                  >
                    {t(s.bodyKey)}
                  </Typography>

                  {idx !== sections.length - 1 ? (
                    <Divider sx={{ my: 3 }} />
                  ) : null}
                </Box>
              ))}
            </Box>

            <Divider />

            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.7 }}
              >
                {t("privacy_disclaimer")}
              </Typography>
            </Box>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
